// Mandatory Discount Types for Philippine Law Compliance
export const MANDATORY_DISCOUNT_TYPES = {
  NONE: 'NONE',
  SENIOR: 'SENIOR',
  PWD: 'PWD'
};

export const MANDATORY_DISCOUNT_LABELS = {
  NONE: 'No Discount',
  SENIOR: 'Senior Citizen (20%)',
  PWD: 'Person with Disability (20%)'
};

// Philippine law discount calculation constants
export const MANDATORY_DISCOUNT_RATE = 0.20; // 20% discount
export const VAT_RATE = 0.12; // 12% VAT in Philippines

/**
 * Calculate mandatory discount amounts for non-VAT registered business
 * @param {number} roomRate - Room rate per night
 * @param {string} discountType - Type of discount (SENIOR or PWD)
 * @param {number} maxCapacity - Maximum capacity of the room
 * @returns {object} Calculation breakdown
 */
export const calculateMandatoryDiscount = (roomRate, discountType, maxCapacity = 1) => {
  if (discountType === MANDATORY_DISCOUNT_TYPES.NONE || !roomRate || !maxCapacity) {
    return {
      baseAmount: roomRate || 0,
      perPersonShare: roomRate || 0,
      vatExemptBase: 0, // Always 0 for non-VAT business
      discountAmount: 0,
      vatExemptAmount: 0, // Always 0 for non-VAT business
      finalAmount: roomRate || 0,
      totalSavings: 0
    };
  }

  // Step 1: Calculate per-person share (room rate ÷ capacity)
  const perPersonShare = roomRate / maxCapacity;
  
  // Step 2: Apply 20% discount directly on per-person share (no VAT calculations for non-VAT business)
  const discountAmount = perPersonShare * MANDATORY_DISCOUNT_RATE;
  
  // Step 3: Final amount = per-person share - 20% discount
  const finalAmount = perPersonShare - discountAmount;
  
  // Total savings = discount only (no VAT exemptions for non-VAT business)
  const totalSavings = discountAmount;

  return {
    baseAmount: roomRate,
    perPersonShare,
    vatExemptBase: 0, // Always 0 for non-VAT business
    discountAmount,
    vatExemptAmount: 0, // Always 0 for non-VAT business
    finalAmount,
    totalSavings
  };
};

/**
 * Example calculation for non-VAT registered business
 * Room: ₱2,000/night, capacity: 2, both guests are SC holders
 * Result: ₱1,600.00 final total
 */
export const getExampleCalculation = () => {
  const roomRate = 2000;
  const capacity = 2;
  const discountType = MANDATORY_DISCOUNT_TYPES.SENIOR;
  
  const calculation = calculateMandatoryDiscount(roomRate, discountType, capacity);
  
  return {
    scenario: "Room: ₱2,000/night, capacity: 2, both guests are SC holders (Non-VAT Business)",
    perPersonShare: calculation.perPersonShare, // ₱1,000
    discountAmount: calculation.discountAmount, // ₱200 (20% of ₱1,000)
    finalPerPerson: calculation.finalAmount,    // ₱800
    finalTotal: calculation.finalAmount * 2,    // ₱1,600
    totalSavings: calculation.totalSavings * 2  // ₱400
  };
};

export default {
  MANDATORY_DISCOUNT_TYPES,
  MANDATORY_DISCOUNT_LABELS,
  MANDATORY_DISCOUNT_RATE,
  VAT_RATE,
  calculateMandatoryDiscount,
  getExampleCalculation
};
